import React from "react";
import { Box } from "@mui/material";
import { DropdownSearchHelpInput, TextInput } from "../../../common/form-controls";
import { reportsService } from "../../../../services/reports-service";
import { useIntl } from "react-intl";
import { usersService } from "../../../../services/users-service";
import { DropdownSearchHelpTagInput } from "../../../common/form-controls/dropdown-search-help-input";
import { PreparationForTestingRequestFormState } from "@/types/brm";

type DropdownSearchHelpInputParams = {
    limit: number;
    page: number;
    search: string;
};
type ManagerInputProps = {
    ids: { key: string; text: string }[];
    setIds: (ids: { key: string; text: string }[]) => void;
    getUsers: (params: DropdownSearchHelpInputParams, signal: AbortSignal) => Promise<object>;
    error?: boolean;
    errorMessage?: string;
    disabledMultiple: boolean;
};

const getUsers = (params: DropdownSearchHelpInputParams, signal: AbortSignal) => {
    return usersService.getUsersForSearchHelp(params, signal);
};
const UsersInput: React.FC<ManagerInputProps> = ({ ids, setIds, error, errorMessage, getUsers, disabledMultiple = false }) => {
    const intl = useIntl();
    const label = intl.formatMessage({ id: "preparation-for-testing-request-form.input-manager" });
    return (
        <DropdownSearchHelpTagInput
            tags={ids}
            setTags={setIds}
            getData={getUsers}
            error={Boolean(error)}
            errorMessage={errorMessage}
            label={label}
            disableMultiple={disabledMultiple}
        />
    );
};

type TestingUserCountInputProps = {
    value: string;
    setValue: (value: string) => void;
    error?: boolean;
    errorMessage?: string;
};
const TestingUserCountInput: React.FC<TestingUserCountInputProps> = ({ value, setValue, error, errorMessage }) => {
    const intl = useIntl();
    const label = intl.formatMessage({ id: "preparation-for-testing-request-form.input-test-users-count" });

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = event.target.value.replace(/\D/g, "");
        setValue(newValue);
    };

    return <TextInput label={label} value={value} onChange={handleChange} error={error} errorMessage={errorMessage} />;
};

const getRoles = (params: DropdownSearchHelpInputParams, signal: AbortSignal) => {
    return reportsService.getRolesSearchHelp(params, signal);
};

type RolesInputProps = {
    ids: string[];
    setIds: (ids: string[]) => void;
    error?: boolean;
    errorMessage?: string;
};

const RolesInput: React.FC<RolesInputProps> = ({ ids, setIds, error, errorMessage }) => {
    const intl = useIntl();
    const label = intl.formatMessage({ id: "preparation-for-testing-request-form.input-additional-roles" });

    return <DropdownSearchHelpInput ids={ids} setIds={setIds} getData={getRoles} label={label} error={error} errorMessage={errorMessage} />;
};

type FormProps = {
    form: PreparationForTestingRequestFormState;
    setForm: (form: Partial<FormProps["form"]>) => void;
    // validationErrors: Record<string, { value: string; message: string }> | null;
    validationErrors: {
        testing_responsible?: {
            additional_info: string;
            code: string;
            field: string;
            message: string;
            type: string;
            value: string[];
        };
        testing_user_count?: {
            additional_info: string;
            code: string;
            field: string;
            message: string;
            type: string;
            value: number;
        };
        additional_roles?: {
            additional_info: string;
            code: string;
            field: string;
            message: string;
            type: string;
            value: string[];
        };
    };
};
const PreparationForTestingRequestForm: React.FC<FormProps> = ({ form, setForm, validationErrors }) => {
    return (
        <Box className="preparation-for-testing-request-form">
            <UsersInput
                ids={form.testingResponsible}
                setIds={(value) => setForm({ testingResponsible: value })}
                error={validationErrors?.testing_responsible && JSON.stringify(validationErrors.testing_responsible.value) === JSON.stringify(form.testingResponsible)}
                errorMessage={validationErrors?.testing_responsible?.message}
                disabledMultiple={false}
                getUsers={getUsers}
            />

            <TestingUserCountInput
                value={form.testingUserCount}
                setValue={(value) => setForm({ testingUserCount: value })}
                error={String(validationErrors?.testing_user_count?.value) === form.testingUserCount}
                errorMessage={validationErrors?.testing_user_count?.message}
            />

            <RolesInput
                ids={form.additionalRoles}
                setIds={(value) => setForm({ additionalRoles: value })}
                error={validationErrors?.additional_roles && JSON.stringify(validationErrors.additional_roles.value) === JSON.stringify(form.additionalRoles)}
                errorMessage={validationErrors?.additional_roles?.message}
            />
        </Box>
    );
};

export default PreparationForTestingRequestForm;
